import { Dispatch } from 'react';
import { ThunkAction } from 'redux-thunk';
import { Actions } from '.';
import { pushPatient } from '../../api';
import fetchAssessmentSummary from '../../api/fetchAssessmentSummary';
import fetchPatientDirByDocOid, {
    Patient as BPPatient,
    Relation,
} from '../../api/fetchPatientDirByDocOid';
import initVisit from '../../api/initVisit';
import { GenieSettings } from '../../api/pushGenie';
import { PushPatientResponse, UpsertPatientResponse } from '../../api/upsertPatient';
import {
    ActiveOrderUpdatesLastUpdatedAt,
    ActiveOrderUpdatesCount,
    PtDirLastUpdatedAt,
    PtDirVersion,
} from '../../constants';
import { GO_TO_SETUP_APP } from '../../routes/paths';
import {
    dumpDynamicFormAutoSuggest,
    isOnboardedForLab,
    isOnboardedForPharma,
} from '../../pages/utils';
import { track } from '../../tracking';
import {
    deleteActiveOrderUpdates,
    deleteAllPatients,
    deletePatientByLocalId,
    getDocIds,
    getItemFromMisc,
    getMe,
    getMySingleDoctorId,
    getPatientByLocalId,
    isIDristi,
    MISC_KEYS,
    updateItemInMisc,
    upsertActiveOrderUpdates,
    upsertPatient,
    upsertPatients,
} from '../idbHandler';
import { GlobalState } from '../reducers';
import { Age, getPtFullName, isLocalPt, Patient } from '../types/patient';
import { generatePatientId } from '../utils';
import { setAssessmentSummary, updateAssessmentSummaryFetchStateAction } from './prescriptionPad';
import { addPatientToIndex, indexAllPatients } from '../../pages/allPatientsIndex';
import { MHC } from '../types/medicalHistory';
import { captureError } from '../../pages/captureError';
import getActiveOrders from '../../api/getActiveOrders';
import { store } from '../../App';
import { setActiveOrderUpdatesCount } from './session';
import { getMyDbId } from '../../api/mactiveid';
import { getActiveDoctorId, getIpdLastUpdated, getMActiveId, setIpdConfig } from '../localstorage';
import getIpdConfigData from '../../api/getIpdConfigData';
import { PtDir } from '../../api/fetchMyGenie';
import { updateAddVisit } from '../../utils/commons';
import fetchPatientsFromTrinity from '../../api/fetchPatientsFromTrinity';
import fetchPatientDirByDocOidV2 from '../../api/fetchPatientDirByDocOidV2';
import { FULL_GENDER_CHARS } from '../../pages/NewPatient';
import { notification } from 'antd';
import { parsePhoneNumber } from 'libphonenumber-js';
import fetchBulkPatientsFromTrinity from '../../api/fetchBulkPatientsFromTrinity';

export type PPA = ThunkAction<Promise<PushPatientResponse | void>, GlobalState, null, Actions>;

export interface NewPatient {
    localId?: string;
    oid?: string;
    name: string;
    phCode?: string;
    phoneNumber?: string;
    age?: Age;
    gender: string;
    bloodgroup?: string;
    onApp?: boolean;
    formData?: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    mh?: MHC;
    refer_doc_id?: string;
    relations?: Relation[];
}

export const addPatient = (props: {
    newPatient: NewPatient;
    visitid?: string;
    sendWelcome: boolean;
    onSuccess?: (patient: Patient & { apiFailure: boolean; error?: Error }) => void;
    oldPtObj?: Patient;
    fromModal?: boolean;
    onError?: (error: string) => void;
    doc?: { id: string };
}): ((dispatch: Dispatch<PPA>) => Promise<Patient | undefined>) => {
    const { newPatient, visitid, sendWelcome, onSuccess, oldPtObj, doc } = props;

    const patientLocalId =
        newPatient.localId ||
        generatePatientId(
            newPatient.phoneNumber || (Math.random() * 10000).toString(),
            newPatient.name,
        );

    const newDocIds = oldPtObj?.docids
        ? Array.from(new Set([...oldPtObj?.docids, doc?.id].filter(Boolean)))
        : doc
        ? [doc.id]
        : [];

    const patient: Patient = {
        ...oldPtObj,

        localId: newPatient.oid || patientLocalId,
        id: newPatient.oid,
        createdAt: new Date(),
        updatedAt: new Date(),
        personal: {
            name: {
                f: newPatient.name,
            },
            phone: newPatient.phoneNumber
                ? {
                      c: newPatient.phCode || '+91',
                      n: newPatient.phoneNumber,
                  }
                : undefined,
            age: newPatient.age,
            gender: newPatient.gender,
        },
        formData: newPatient.formData,
        refer_doc_id: newPatient.refer_doc_id,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore TS doesn't understand that we are filtering out undefined values
        docids: newDocIds,
        relations: newPatient.relations,
    };

    return async (): Promise<Patient | undefined> => {
        const localPt: Patient | undefined = await getPatientByLocalId(patientLocalId);
        if (localPt && localPt.relations) {
            patient.relations = localPt.relations;
        }
        // await upsertPatient(patient);

        return pushPatient(patient)
            .then(async (res) => {
                if (!res) {
                    throw new Error('No response from pushPatient');
                }
                const up = {
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    ...patient,

                    ...(res.oid ? toLocalPatientFromTrinity(res) : {}),
                    id: res.oid || patient.id,
                    localId: res.oid || patient.localId,
                };

                // if (isLocalPt(patient.localId)) {
                //     await deletePatientByLocalId(patient.localId);
                // }
                if (localPt && localPt.relations) {
                    up.relations = localPt.relations;
                }
                // update oid
                await upsertPatient(up);

                onSuccess && onSuccess({ ...up, apiFailure: false });

                return up;
            })
            .catch((e) => {
                // onSuccess && onSuccess({ ...patient, apiFailure: true, error: e });
                console.error('pushPatient =>', e);
                props.onError?.(e.message);
                return patient;
            });
    };
};

export const toLocalPatientFromTrinity = (bp: UpsertPatientResponse): Patient => {
    const phone = bp.mobile ? parsePhoneNumber(bp.mobile) : undefined;

    return {
        id: bp.oid,
        localId: bp.oid,
        uuid: bp.uuid,
        payments: bp?.extras?.payments,
        personal: {
            name: {
                f: bp.fln,
            },
            phone: {
                c: phone?.countryCallingCode ? `+${phone.countryCallingCode}` : '+91',
                n: phone?.nationalNumber,
            },
            age: {
                dob: bp.dob,
            },
            gender: FULL_GENDER_CHARS[bp?.gen?.toUpperCase() || ''] || '',
            'health-ids': bp['health-ids'],
        },
        createdAt: new Date(bp.at),
        updatedAt: new Date(bp.uat || bp.at),
        formData: bp.extras?.formData,
        archived: bp.deact || false,
        link: bp.extras?.link || [],
        abha: bp.extras?.abha,
        abhaV2: bp.extras?.abhaV2,
        referredBy: bp?.extras?.referredBy,
        refer_id: bp?.extras?.refer_id,
        relations: bp?.extras?.relations,
    };
};

export const toLocalPatient = (bp: BPPatient): Patient => ({
    id: bp.id,
    localId: bp.id,
    uuid: bp.uuid,
    poid: bp.poid,
    payments: bp.payments,
    personal: {
        name: {
            f: bp.profile.personal.name,
        },
        phone: bp.profile.personal.phone,
        age: bp.profile.personal.age,
        gender: bp.profile.personal.gender || '',
        onApp: bp.profile.personal.onApp,
        'health-ids': bp.profile.personal['health-ids'],
    },
    createdAt: new Date(bp.created_at),
    updatedAt: new Date(bp.updated_at),
    lastVisitedAt: bp.last_visit ? new Date(bp.last_visit) : undefined,
    followUpDate: bp.followup ? new Date(bp.followup) : undefined,
    bloodgroup: bp.profile.personal.bloodgroup || undefined,
    formData: bp.formData,
    archived: bp.archived || false,
    link: bp.link || [],
    abha: bp.abha,
    abhaV2: bp.abhaV2,
    referredBy: bp?.referredBy,
    refer_id: bp?.refer_id,
    docids: bp.docids,
    relations: bp.relations,
});

export const fetchPtDir = async (onSuccess?: () => void, onFailure?: () => void): Promise<void> => {
    try {
        const myDbId = getActiveDoctorId() || getMActiveId();

        const settings = await getItemFromMisc<PtDir>(getMActiveId() || '', 'ptDir');

        if (settings.disableAllPtFetch) {
            console.info('All pt fetch is disabled');
            return;
        }

        const bid = getMe()?.user['b-id'];

        if (!bid) {
            track('ERROR', {
                what: 'NO_BID',
                on: 'fetchPtDir',
                message: 'No b-id found',
            });
            console.error('No b-id found');
            return;
        }

        if (getMedantaBizId().includes(bid)) {
            track('WARNING_trying_fetch_all_pt_medanta', {
                on: 'fetchPtDir',
                message: 'No b-id found',
            });
            return;
        }

        if (!getDocIds().length || !myDbId) {
            console.error('doc list not found', { list: getDocIds(), myDbId });
            captureError(
                new Error(`doc list not found ${JSON.stringify({ list: getDocIds(), myDbId })}`),
            );
            return;
        }

        const lastUpdated = await getItemFromMisc<PtDirLastUpdatedAt>(
            myDbId,
            MISC_KEYS.ptDirLastUpdated,
        );

        let patients: Patient[] = [];

        if (isIDristi()) {
            let pageNo = 1;
            const pageSize = 2000;
            const promises = [];
            console.log('fetching pt dir', { pageSize, pageNo });
            const currentVersion = await getItemFromMisc<PtDirVersion>(
                myDbId,
                MISC_KEYS.ptDirVersion,
            );

            while (true) {
                const ptsResponse = await fetchPatientsFromTrinity(
                    !settings.userLevel ? ['all'] : getDocIds(),
                    undefined,
                    lastUpdated.isoString,
                    pageNo.toString(),
                    pageSize.toString(),
                );

                if (!ptsResponse.data) {
                    console.error('not pt found in pt dir');
                    return;
                }

                if (!ptsResponse.data?.length || ptsResponse.data.length < pageSize) {
                    console.log('fetching pt dir done', { pageSize, pageNo });
                    break;
                }

                if (currentVersion.version && currentVersion.version !== ptsResponse.version) {
                    track('VERSION_UPDATE', {
                        on: 'fetchPtDir',
                        currentVersion: currentVersion.version?.toString(),
                        ptDirVersion: ptsResponse.version?.toString(),
                    });
                    // delete all
                    await deleteAllPatients();
                    await updateItemInMisc(myDbId, MISC_KEYS.ptDirLastUpdated, {
                        isoString: undefined,
                    });
                    await updateItemInMisc(myDbId, MISC_KEYS.ptDirVersion, { version: undefined });
                    alert("You've got a new version of the application. Please refresh the page.");
                    window.location.replace(GO_TO_SETUP_APP);
                    return;
                }

                const localPts: Patient[] = ptsResponse.data.map(toLocalPatient);
                patients = localPts;
                promises.push(upsertPatients(localPts));

                promises.push(
                    updateItemInMisc<PtDirVersion>(myDbId, MISC_KEYS.ptDirVersion, {
                        version: ptsResponse.version,
                    }),
                );

                dumpDynamicFormAutoSuggest(localPts);
                pageNo++;
            }

            if (!lastUpdated.isoString) {
                await indexAllPatients();
            } else {
                await addPatientToIndex(patients);
            }
            await Promise.all(promises);

            await updateItemInMisc<PtDirLastUpdatedAt>(myDbId, MISC_KEYS.ptDirLastUpdated, {
                isoString: new Date().toISOString(),
            });

            onSuccess && onSuccess();

            return;
        }

        const ptsResponse = await fetchPatientDirByDocOid(
            !settings.userLevel ? ['all'] : getDocIds(),
            undefined,
            lastUpdated.isoString,
        );

        if (!ptsResponse.data) {
            console.error('not pt found in pt dir');
            return;
        }

        const currentVersion = await getItemFromMisc<PtDirVersion>(myDbId, MISC_KEYS.ptDirVersion);

        if (currentVersion.version && currentVersion.version !== ptsResponse.version) {
            track('VERSION_UPDATE', {
                on: 'fetchPtDir',
                currentVersion: currentVersion.version?.toString(),
                ptDirVersion: ptsResponse.version?.toString(),
            });
            // delete all
            await deleteAllPatients();
            await updateItemInMisc(myDbId, MISC_KEYS.ptDirLastUpdated, { isoString: undefined });
            await updateItemInMisc(myDbId, MISC_KEYS.ptDirVersion, { version: undefined });
            alert("You've got a new version of the application. Please refresh the page.");
            window.location.replace(GO_TO_SETUP_APP);
            return;
        }

        const localPts: Patient[] = ptsResponse.data.map(toLocalPatient);
        await upsertPatients(localPts);
        await updateItemInMisc<PtDirLastUpdatedAt>(myDbId, MISC_KEYS.ptDirLastUpdated, {
            isoString: new Date().toISOString(),
        });

        await updateItemInMisc<PtDirVersion>(myDbId, MISC_KEYS.ptDirVersion, {
            version: ptsResponse.version,
        });

        if (!lastUpdated.isoString) {
            await indexAllPatients();
        } else {
            await addPatientToIndex(localPts);
        }

        dumpDynamicFormAutoSuggest(localPts);
        onSuccess && onSuccess();
    } catch (e) {
        console.error('Error fetchPtDir=>', e);
        onFailure && onFailure();
    }
};

export const triggerStartVisitOnBE = ({
    visitId,
    startedAt,
    clinicId,
    docId,
    patient,
    pid,
    abortVisitId,
}: {
    visitId: string;
    startedAt: string;
    clinicId: string;
    docId: string;
    patient?: NewPatient;
    pid?: string;
    abortVisitId?: string;
}): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return (): Promise<void> => {
        return initVisit({
            aid: visitId,
            startedAt,
            clinicId,
            docId,
            patient,
            pid,
            abortVisitId,
        }).catch((e) => {
            console.error('failed to initVisit =>', e);
        });
    };
};

export const fetchAndCacheAssessmentSummary = ({
    visitId,
}: {
    visitId: string;
}): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        try {
            dispatch(updateAssessmentSummaryFetchStateAction('loading'));
            const res = await fetchAssessmentSummary(visitId);

            if (res.status !== 'success') {
                dispatch(updateAssessmentSummaryFetchStateAction('error'));
                return;
            }

            res.response
                ? dispatch(setAssessmentSummary(res.response))
                : dispatch(updateAssessmentSummaryFetchStateAction('error'));
        } catch (e) {
            console.error('fetchAndCacheAssessmentSummary=>', e);
            dispatch(updateAssessmentSummaryFetchStateAction('error'));
        }
    };
};

export const fetchAndStoreIpdData = (): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        try {
            const res = await getIpdConfigData(getIpdLastUpdated());
            if (res.success) {
                console.log('IPD data fetched successfully');
                setIpdConfig(res.data);
            }
        } catch (e) {
            console.error('fetchAndCacheAssessmentSummary=>', e);
            dispatch(updateAssessmentSummaryFetchStateAction('error'));
        }
    };
};

type PtWithId = Patient & { id: string };

export const WithBEPatient = <T>(
    p: Patient,
    task: (bePt: PtWithId) => Promise<T>,
): ((dispatch: Dispatch<PPA>) => Promise<T>) => {
    return async (dispatch) => {
        if (p.id) {
            return task(p as PtWithId);
        }

        const onSuccess = () => {
            return;
        };

        const settings = await getItemFromMisc<GenieSettings>(getMyDbId() || '', 'settings');

        const bePatient: Patient | undefined = await addPatient({
            newPatient: {
                localId: p.localId,
                oid: p.id,
                name: getPtFullName(p),
                phoneNumber: p.personal.phone?.n,
                age: p.personal.age,
                gender: p.personal.gender,
                bloodgroup: p.bloodgroup,
                onApp: p.personal.onApp,
                formData: p.formData,
            },
            onSuccess,
            sendWelcome: settings.sendWelcome,
        })(dispatch);

        if (bePatient && bePatient.id) {
            track('GENERATED_ID_FOR_OFFLINE_PT');
            return task(bePatient as PtWithId);
        }

        return Promise.reject('Patient id not found');
    };
};

const getCurrentDayStartTime = (): number => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm: number | string = today.getMonth() + 1;
    if (mm < 10) mm = '0' + mm;
    let dd: number | string = today.getDate();
    if (dd < 10) dd = '0' + dd;
    const formattedDate = `${yyyy}-${mm}-${dd}`;
    return new Date(formattedDate).getTime() / 1000;
};

export const fetchActiveOrderUpdates = async (): Promise<void> => {
    if (!isOnboardedForPharma() && !isOnboardedForLab()) return;
    try {
        const lastActiveFrom = await getItemFromMisc<ActiveOrderUpdatesLastUpdatedAt>(
            getMySingleDoctorId() || '',
            MISC_KEYS.activeOrderUpdatesLastUpdated,
        );
        const currentDayStartTime = getCurrentDayStartTime();
        const isOutdatedTime = Number(lastActiveFrom.active_from || 0) < currentDayStartTime;
        if (isOutdatedTime) {
            await deleteActiveOrderUpdates();
            await updateItemInMisc<ActiveOrderUpdatesCount>(
                getMySingleDoctorId() || '',
                MISC_KEYS.activeOrderUpdatesCount,
                {
                    count: 0,
                },
            );
            store.dispatch(setActiveOrderUpdatesCount(0));
        }
        const activeFrom = isOutdatedTime
            ? currentDayStartTime
            : Number(lastActiveFrom.active_from || 0);
        const docIds = getDocIds().join(',');
        const { orders } = await getActiveOrders(docIds, activeFrom, undefined, undefined);
        if (!orders || orders.length === 0) {
            console.error('No active orders found');
        } else {
            await upsertActiveOrderUpdates(orders);
        }
        const newActiveFrom = new Date().getTime() / 1000;
        await updateItemInMisc<ActiveOrderUpdatesLastUpdatedAt>(
            getMySingleDoctorId() || '',
            MISC_KEYS.activeOrderUpdatesLastUpdated,
            {
                active_from: newActiveFrom,
            },
        );
        await updateItemInMisc<ActiveOrderUpdatesCount>(
            getMySingleDoctorId() || '',
            MISC_KEYS.activeOrderUpdatesCount,
            {
                count: orders?.length,
            },
        );
        store.dispatch(setActiveOrderUpdatesCount(orders?.length || 0));
    } catch (e) {
        console.error('Error fetchActiveOrderUpdates=>', e);
        captureError(e as Error);
    }
};

export const fetchPatientsList = async (pids: string[]): Promise<void> => {
    try {
        const myDbId = getActiveDoctorId() || getMActiveId();

        if (!getDocIds().length || !myDbId) {
            console.error('doc list not found', { list: getDocIds(), myDbId });
            captureError(
                new Error(`doc list not found ${JSON.stringify({ list: getDocIds(), myDbId })}`),
            );
            return;
        }

        const lastUpdated = await getItemFromMisc<PtDirLastUpdatedAt>(
            myDbId,
            MISC_KEYS.ptDirLastUpdated,
        );

        const ptsResponse = await fetchPatientDirByDocOidV2(['all'], pids.join(','));

        if (!ptsResponse.data) {
            console.error('not pt found in pt dir');
            return;
        }

        const currentVersion = await getItemFromMisc<PtDirVersion>(myDbId, MISC_KEYS.ptDirVersion);

        if (currentVersion.version && currentVersion.version !== ptsResponse.version) {
            track('VERSION_UPDATE', {
                on: 'fetchPtDir',
                currentVersion: currentVersion.version?.toString(),
                ptDirVersion: ptsResponse.version?.toString(),
            });
            // delete all
            await deleteAllPatients();
            await updateItemInMisc(myDbId, MISC_KEYS.ptDirLastUpdated, { isoString: undefined });
            await updateItemInMisc(myDbId, MISC_KEYS.ptDirVersion, { version: undefined });
            alert("You've got a new version of the application. Please refresh the page.");
            window.location.replace(GO_TO_SETUP_APP);
            return;
        }

        const localPts: Patient[] = ptsResponse.data.map(toLocalPatient);

        await upsertPatients(localPts);
        await updateItemInMisc<PtDirVersion>(myDbId, MISC_KEYS.ptDirVersion, {
            version: ptsResponse.version,
        });

        ptsResponse.data.reduce(
            (upserts, pt) =>
                pt.visits
                    ? upserts.concat(
                          pt.visits.map((v) => {
                              if (v.status === 'BK') {
                                  return;
                              }
                              return updateAddVisit(pt, v);
                          }),
                      )
                    : upserts,
            [] as (Promise<void> | void)[],
        );

        if (!lastUpdated.isoString) {
            await indexAllPatients();
        } else {
            await addPatientToIndex(localPts);
        }

        dumpDynamicFormAutoSuggest(localPts);
    } catch (e) {
        console.error('Error fetchPtDir=>', e);
    }
};

export const fetchPatientsListV2 = async (pids: string[]): Promise<void> => {
    if (!pids.length) {
        console.error('No pids found');
        return;
    }
    const uniquePids = Array.from(new Set(pids));
    try {
        // create batch of 25 and call fetchBulkPatientsFromTrinity
        const batchSize = 25;
        const batches = [];
        for (let i = 0; i < uniquePids.length; i += batchSize) {
            const batch = uniquePids.slice(i, i + batchSize);
            batches.push(batch);
        }

        const promizes = batches.map(fetchBulkPatientsFromTrinity);
        const results = await Promise.all(promizes);

        const localPts: Patient[] = results.flat().map(toLocalPatientFromTrinity);

        await upsertPatients(localPts);

        await addPatientToIndex(localPts);

        dumpDynamicFormAutoSuggest(localPts);
    } catch (e) {
        notification.error({
            message: 'Delay in fetching patients, click on refresh to try again',
            description: (e as Error).message,
            duration: 5,
        });
        track('ERROR', {
            what: 'fetch pt catch',
            on: 'fetchPatientsList',
            message: 'Error fetching patients',
            error: (e as Error).message,
            pids: pids.toString(),
        });
        captureError(e as Error);
        console.error('Error fetchPtDir=>', e);
    }
};

export function getMedantaBizId(): string[] {
    return ['76527282322352', '76535964545360', '77133577914507', '7174763797343791'];
}
